import { useState, useEffect, useRef } from 'react';
import { Button } from "@mui/material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import MenuPanel from "../components/MenuPanel";
import SecondFooter from "../components/SecondFooter";
import Footer from "../components/Footer";
import { LoginModal } from "../components/LoginModal";
import CertificatePage from "../components/CertificatePage";
import { FormattedMessage, useIntl } from "react-intl";
import { setLanguage } from "../Redux/languageSlice";
import {
  Text,
  Title,
  TextInput,
  Button as MantineButton,
  Box,
  Container,
  Tabs,
  Table,
  ScrollArea,
  Badge,
  Group,
  Card,
  SimpleGrid,
  Loader
} from "@mantine/core";
import { FaSearch, FaMapMarkedAlt, FaHistory, FaCertificate } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { getFetchLedgerLot, getFetchLedgerLotHistory } from "../Redux/products/slice";
import { BASE_URL } from '../Redux/apiConfig';

// For map visualization

import { 
  ComposableMap, 
  Geographies, 
  Geography, 
  Marker,
  ZoomableGroup
} from "react-simple-maps";

// Sample map data - production volumes by location
const sampleMapData = [
  { name: "Paris", coordinates: [2.3522, 48.8566], volume: 2500, color: "#FF5733" },
  { name: "New York", coordinates: [-74.0060, 40.7128], volume: 3400, color: "#33FF57" },
  { name: "Beijing", coordinates: [116.4074, 39.9042], volume: 1800, color: "#3357FF" },
  { name: "Berlin", coordinates: [13.4050, 52.5200], volume: 1250, color: "#FF33A8" },
  { name: "São Paulo", coordinates: [-46.6333, -23.5505], volume: 2100, color: "#33FFF6" },
  { name: "Cairo", coordinates: [31.2357, 30.0444], volume: 1600, color: "#F6FF33" },
  { name: "Mumbai", coordinates: [72.8777, 19.0760], volume: 1900, color: "#A833FF" },
  { name: "Tokyo", coordinates: [139.6917, 35.6895], volume: 3100, color: "#FF9433" },
  { name: "Sydney", coordinates: [151.2093, -33.8688], volume: 1400, color: "#33FFD8" },
  { name: "Moscow", coordinates: [37.6173, 55.7558], volume: 2200, color: "#8DFF33" },
];

const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: 0,
    margin: 0,
  },
  header: {
    width: '100%',
    padding: '0px 0',
  },
  quoteSection: {
    textAlign: 'center',
    padding: '40px 0 20px',
    borderBottom: '1px solid #e9ecef',
    marginBottom: '40px',
    background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
  },
  quoteText: {
    fontSize: '2.2rem',
    fontWeight: 700,
    color: '#2C3E50',
    margin: 0,
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: '-0.5px',
  },
  descriptionText: {
    fontSize: '1.1rem',
    color: '#5D6D7E',
    maxWidth: '800px',
    margin: '20px auto 0',
    fontFamily: "'Montserrat', sans-serif",
    lineHeight: 1.6,
  },
  sectionDivider: {
    height: '4px',
    width: '100px',
    margin: '1.5rem auto 3rem',
    background: 'linear-gradient(90deg, #3498db, #2980b9)',
    borderRadius: '2px',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 20px 40px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    maxWidth: '500px',
    width: '100%',
    margin: '0 auto 30px',
  },
  resultContainer: {
    width: '100%',
    maxWidth: '1000px',
    margin: '20px auto',
  },
  copyButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '30px',
    width: '100%',
  },
  footerSection: {
    marginTop: '60px',
    borderTop: '1px solid #e9ecef',
    paddingTop: '20px',
  },
  tabsContainer: {
    marginTop: '20px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '& .mantine-Tabs-tab': {
      fontSize: '1rem',
      fontWeight: 600,
      padding: '12px 24px',
      transition: 'all 0.3s ease',
      '&[data-active]': {
        borderColor: '#3498db',
        color: '#3498db',
      },
      '&:hover': {
        backgroundColor: '#f0f8ff',
      },
    },
  },
  mapContainer: {
    width: '100%',
    height: '500px',
    margin: '20px 0',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#f0f0f0',
  },
  mapLoaderContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.7)',
    zIndex: 10
  },
  transactionList: {
    width: '100%',
    marginTop: '20px',
  },
  statsCard: {
    padding: '20px',
    backgroundColor: 'white',
    minHeight: '140px',  // Increased from 120px
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between', // Proper spacing
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    },
  }
};

const EnhancedTransactionExplorer = () => {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');
  const [tooltipContent, setTooltipContent] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const scrollViewportRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [geoData, setGeoData] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const selectedLanguage = useSelector((state) => state.language);
  const [hasSearched, setHasSearched] = useState(false); 

  // First, add a new state variable to store the transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [mapData, setMapData] = useState(sampleMapData);
  const [transactionPage, setTransactionPage] = useState(0);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const network = BASE_URL.includes('vertobackendappdev') ? 'testnet' : 'mainnet';

  // Enable/disable translation - set to false if translation service is not running
  const ENABLE_TRANSLATION = false;

  //Translation function
  const translateText = async (text, targetLang) => {
      if (!ENABLE_TRANSLATION) return text;
      if (!text) return text;
      try {
        const response = await fetch("http://localhost:4000/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, targetLang })
        });
        
        if (!response.ok) {
          console.warn("Translation service unavailable, returning original text");
          return text;
        }
        
        const data = await response.json();
        return data.translations?.[0]?.text || text;
      } catch (error) {
        // Silently fail and return original text if translation service is down
        console.warn("Translation service not available:", error.message);
        return text;
     }
  };
  const translateTransactions = async (transactions, targetLang) => {
  const result = [];
  for (const tx of transactions) {
    const translatedTx = { ...tx };
    if (tx.categoryName) translatedTx.categoryName = await translateText(tx.categoryName, targetLang);
    if (tx.productName) translatedTx.productName = await translateText(tx.productName, targetLang);
    if (tx.QuantityUOM) translatedTx.QuantityUOM = await translateText(tx.QuantityUOM, targetLang);
    if (tx.location) translatedTx.location = await translateText(tx.location, targetLang);
    if (tx.transportDescription) translatedTx.transportDescription = await translateText(tx.transportDescription, targetLang);
    result.push(translatedTx);
  }
  return result;
  };

   // Add a function to fetch transactions from your backend
  const fetchLatestTransactions = async (page = 0, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setTransactionsLoading(true);
    }
    try {
      // Set up query parameters if needed (e.g., count)
      let params = new URLSearchParams({
        count: 20,
        offset: page * 20
      }).toString();
      // Configure fetch options to match your API pattern
      let options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "abc123",
          apikey: "abc123",
        },
      };

      // Make the API request
      const response = await fetch(`${BASE_URL}/getLatestTransactions?${params}`, options);
      const data = await response.json();

      // Assuming your backend returns data in the format { status: "Success", result: [...] }
      if (data.status === "Success" && Array.isArray(data.result)) {
        let filteredTransactions = data.result.filter(item => item.event !== 'updateContract');
        filteredTransactions = await translateTransactions(filteredTransactions, selectedLanguage);
        console.log("filteredTransactions",filteredTransactions)
        
        if (append) {
          setTransactions(prev => [...prev, ...filteredTransactions]);
        } else {
          setTransactions(filteredTransactions);
        }
        
        // If we got less than 20 transactions, there are no more
        setHasMoreTransactions(filteredTransactions.length === 20);
      } else {
        console.error('Unexpected response format:', data);
        setHasMoreTransactions(false);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setHasMoreTransactions(false);
    } finally {
      setTransactionsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Function to load more transactions
  const loadMoreTransactions = () => {
    if (!isLoadingMore && hasMoreTransactions) {
      const nextPage = transactionPage + 1;
      setTransactionPage(nextPage);
      fetchLatestTransactions(nextPage, true);
    }
  };

  // Call fetchLatestTransactions when the transactions tab is activated
  useEffect(() => {
    if (activeTab === 'transactions') {
      setTransactionPage(0);
      setHasMoreTransactions(true);
      fetchLatestTransactions(0, false);
    }
  }, [selectedLanguage, activeTab] );

  // Add scroll event listener for infinite scroll
  useEffect(() => {
    if (activeTab !== 'transactions') return;
    
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      // Load more when user is 100px from bottom
      if (scrollHeight - scrollTop - clientHeight < 100) {
        if (!isLoadingMore && hasMoreTransactions) {
          loadMoreTransactions();
        }
      }
    };

    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [activeTab, isLoadingMore, hasMoreTransactions, transactionPage]);
  const dispatch = useDispatch();
  const intl = useIntl();

  // Load world map data from public URL when map tab is activated
  useEffect(() => {
    if (activeTab === 'map' && !geoData) {
      setIsMapLoading(true);
      // Load a pre-built GeoJSON from a CDN
      fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json")
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          setGeoData(data);
          setIsMapLoading(false);
        })
        .catch(error => {
          console.error("Failed to load world GeoJSON:", error);
          setIsMapLoading(false);
        });
    }
  }, [activeTab, geoData]);

  // Function to fetch and process transaction data for the map
  const fetchMapData = async () => {
    setIsMapLoading(true);
    try {
      // Set up query parameters - request more transactions for map visualization
      let params = new URLSearchParams({
        count: 200 // Request more transactions for better map visualization
      }).toString();
      // Configure fetch options
      let options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "abc123",
          apikey: "abc123",
        },
      };

      // Make the API request
      const response = await fetch(`${BASE_URL}/getLatestTransactions?${params}`, options);
      const data = await response.json();
      console.log(" API response fetchMapData:", data);

      if (data.status === "Success" && Array.isArray(data.result)) {
        // Filter transactions to include only addLot events
        const addLotTransactions = data.result.filter(tx =>
          tx.event === "addLot" ||
          (tx?.physicalState === "Harvested" ||
           tx?.physicalState === "Cosechado" ||
           tx?.physicalState === "Recolté")
        );

        if (addLotTransactions.length === 0) {
          // If no addLot events, fall back to sample data
          // setMapData(sampleMapData);
          setIsMapLoading(false);
          return;
        }

        // Process transactions into map data
        const countryMap = new Map();

        // Define a comprehensive mapping of countries to coordinates
        const countryCoordinates = {
          "Afghanistan": [67.7100, 33.9391],
          "Albania": [20.1683, 41.1533],
          "Algeria": [1.6596, 28.0339],
          "Andorra": [1.5218, 42.5063],
          "Angola": [17.8739, -11.2027],
          "Argentina": [-63.6167, -38.4161],
          "Armenia": [44.9299, 40.2890],
          "Australia": [133.7751, -25.2744],
          "Austria": [14.5501, 47.5162],
          "Azerbaijan": [47.5769, 40.1431],
          "Bahamas": [-77.3963, 25.0343],
          "Bahrain": [50.5577, 26.0667],
          "Bangladesh": [90.3563, 23.6850],
          "Barbados": [-59.5432, 13.1939],
          "Belarus": [27.9534, 53.7098],
          "Belgium": [4.4699, 50.5039],
          "Belize": [-88.4976, 17.1899],
          "Benin": [2.3158, 9.3077],
          "Bhutan": [90.4336, 27.5142],
          "Bolivia": [-63.5887, -16.2902],
          "Bosnia and Herzegovina": [17.6791, 43.9159],
          "Botswana": [24.6849, -22.3285],
          "Brazil": [-51.9253, -14.2350],
          "Brunei": [114.7277, 4.5353],
          "Bulgaria": [25.4858, 42.7339],
          "Burkina Faso": [-1.6880, 12.2383],
          "Burundi": [29.9189, -3.3731],
          "Cambodia": [104.9910, 12.5657],
          "Cameroon": [12.3547, 7.3697],
          "Canada": [-106.3468, 56.1304],
          "Cape Verde": [-23.6051, 15.1201],
          "Central African Republic": [20.9394, 6.6111],
          "Chad": [18.7322, 15.4542],
          "Chile": [-71.5430, -35.6751],
          "China": [104.1954, 35.8617],
          "Colombia": [-74.2973, 4.5709],
          "Comoros": [43.3333, -11.6455],
          "Congo": [15.2827, -0.2280],
          "Costa Rica": [-83.7534, 9.7489],
          "Croatia": [15.2, 45.1],
          "Cuba": [-77.7812, 21.5218],
          "Cyprus": [33.4299, 35.1264],
          "Czech Republic": [15.4730, 49.8175],
          "Denmark": [9.5018, 56.2639],
          "Djibouti": [42.5903, 11.8251],
          "Dominica": [-61.3710, 15.4150],
          "Dominican Republic": [-70.1627, 18.7357],
          "Ecuador": [-78.1834, -1.8312],
          "Egypt": [30.8025, 26.8206],
          "El Salvador": [-88.8965, 13.7942],
          "Equatorial Guinea": [10.2679, 1.6508],
          "Eritrea": [39.7823, 15.1794],
          "Estonia": [25.0136, 58.5953],
          "Eswatini": [31.4659, -26.5225],
          "Ethiopia": [40.4897, 9.1450],
          "Fiji": [178.0650, -17.7134],
          "Finland": [25.7482, 61.9241],
          "France": [2.2137, 46.2276],
          "Gabon": [11.6094, -0.8037],
          "Gambia": [-15.3101, 13.4432],
          "Georgia": [43.3569, 42.3154],
          "Germany": [10.4515, 51.1657],
          "Ghana": [-1.0232, 7.9465],
          "Greece": [21.8243, 39.0742],
          "Grenada": [-61.6790, 12.1165],
          "Guatemala": [-90.2308, 15.7835],
          "Guinea": [-9.6966, 9.9456],
          "Guinea-Bissau": [-15.1804, 11.8037],
          "Guyana": [-58.9399, 4.8604],
          "Haiti": [-72.2852, 18.9712],
          "Honduras": [-86.2419, 15.1990],
          "Hungary": [19.5033, 47.1625],
          "Iceland": [-19.0208, 64.9631],
          "India": [78.9629, 20.5937],
          "Indonesia": [113.9213, -0.7893],
          "Iran": [53.6880, 32.4279],
          "Iraq": [43.6793, 33.2232],
          "Ireland": [-8.2439, 53.4129],
          "Israel": [34.8516, 31.0461],
          "Italy": [12.5674, 41.8719],
          "Jamaica": [-77.2975, 18.1096],
          "Japan": [138.2529, 36.2048],
          "Jordan": [36.2384, 30.5852],
          "Kazakhstan": [66.9237, 48.0196],
          "Kenya": [37.9062, 0.0236],
          "Kiribati": [-157.3768, 1.8708],
          "Kosovo": [20.9030, 42.6026],
          "Kuwait": [47.4818, 29.3117],
          "Kyrgyzstan": [74.7661, 41.2044],
          "Laos": [102.4955, 19.8563],
          "Latvia": [24.6032, 56.8796],
          "Lebanon": [35.8623, 33.8547],
          "Lesotho": [28.2336, -29.6100],
          "Liberia": [-9.4295, 6.4281],
          "Libya": [17.2283, 26.3351],
          "Liechtenstein": [9.5537, 47.1660],
          "Lithuania": [23.8813, 55.1694],
          "Luxembourg": [6.1296, 49.8153],
          "Madagascar": [46.8691, -18.7669],
          "Malawi": [34.3015, -13.2543],
          "Malaysia": [101.9758, 4.2105],
          "Maldives": [73.2207, 3.2028],
          "Mali": [-3.9962, 17.5707],
          "Malta": [14.3754, 35.9375],
          "Marshall Islands": [171.1845, 7.1315],
          "Mauritania": [-10.9408, 21.0079],
          "Mauritius": [57.5522, -20.3484],
          "Mexico": [-102.5528, 23.6345],
          "Micronesia": [150.5508, 7.4256],
          "Moldova": [28.3699, 47.4116],
          "Monaco": [7.4246, 43.7384],
          "Mongolia": [103.8467, 46.8625],
          "Montenegro": [19.3744, 42.7087],
          "Morocco": [-7.0926, 31.7917],
          "Mozambique": [35.5296, -18.6657],
          "Myanmar": [95.9560, 21.9162],
          "Namibia": [18.4904, -22.9576],
          "Nauru": [166.9315, -0.5228],
          "Nepal": [84.1240, 28.3949],
          "Netherlands": [5.2913, 52.1326],
          "New Zealand": [174.8860, -40.9006],
          "Nicaragua": [-85.2072, 12.8654],
          "Niger": [8.0817, 17.6078],
          "Nigeria": [8.6753, 9.0820],
          "North Korea": [127.5101, 40.3399],
          "North Macedonia": [21.7453, 41.6086],
          "Norway": [8.4689, 60.4720],
          "Oman": [55.9754, 21.4735],
          "Pakistan": [69.3451, 30.3753],
          "Palau": [134.5825, 7.5150],
          "Palestine": [35.2332, 31.9522],
          "Panama": [-80.7821, 8.5380],
          "Papua New Guinea": [143.9555, -6.3148],
          "Paraguay": [-58.4438, -23.4425],
          "Peru": [-75.0152, -9.1900],
          "Philippines": [121.7740, 12.8797],
          "Poland": [19.1451, 51.9194],
          "Portugal": [-8.2245, 39.3999],
          "Qatar": [51.1839, 25.3548],
          "Romania": [24.9668, 45.9432],
          "Russia": [105.3188, 61.5240],
          "Rwanda": [29.8739, -1.9403],
          "Saint Kitts and Nevis": [-62.7830, 17.3578],
          "Saint Lucia": [-60.9789, 13.9094],
          "Saint Vincent and the Grenadines": [-61.1968, 13.2528],
          "Samoa": [-172.1046, -13.7590],
          "San Marino": [12.4578, 43.9424],
          "São Tomé and Príncipe": [6.6131, 0.1864],
          "Saudi Arabia": [45.0792, 23.8859],
          "Senegal": [-14.4524, 14.4974],
          "Serbia": [21.0059, 44.0165],
          "Seychelles": [55.4920, -4.6796],
          "Sierra Leone": [-11.7799, 8.4606],
          "Singapore": [103.8198, 1.3521],
          "Slovakia": [19.6990, 48.6690],
          "Slovenia": [14.9955, 46.1512],
          "Solomon Islands": [160.1562, -9.6457],
          "Somalia": [46.1996, 5.1521],
          "South Africa": [22.9375, -30.5595],
          "South Korea": [127.7669, 35.9078],
          "South Sudan": [31.3070, 6.8770],
          "Spain": [-3.7492, 40.4637],
          "Sri Lanka": [80.7718, 7.8731],
          "Sudan": [30.2176, 12.8628],
          "Suriname": [-56.0278, 3.9193],
          "Sweden": [18.6435, 60.1282],
          "Switzerland": [8.2275, 46.8182],
          "Syria": [38.9968, 34.8021],
          "Taiwan": [120.9605, 23.6978],
          "Tajikistan": [71.0167, 38.8610],
          "Tanzania": [34.8888, -6.3690],
          "Thailand": [100.9925, 15.8700],
          "Timor-Leste": [125.7275, -8.8742],
          "Togo": [0.8248, 8.6195],
          "Tonga": [-175.1982, -21.1790],
          "Trinidad and Tobago": [-61.2225, 10.6918],
          "Tunisia": [9.5375, 33.8869],
          "Turkey": [35.2433, 38.9637],
          "Turkmenistan": [59.5563, 38.9697],
          "Tuvalu": [177.6493, -7.1095],
          "Uganda": [32.2903, 1.3733],
          "Ukraine": [31.1656, 48.3794],
          "United Arab Emirates": [53.8478, 23.4241],
          "UAE": [53.8478, 23.4241],
          "United Kingdom": [-3.4360, 55.3781],
          "UK": [-3.4360, 55.3781],
          "USA": [-95.7129, 37.0902],
          "United States": [-95.7129, 37.0902],
          "Uruguay": [-55.7658, -32.5228],
          "Uzbekistan": [64.5853, 41.3775],
          "Vanuatu": [166.9592, -15.3767],
          "Venezuela": [-66.5897, 6.4238],
          "Vietnam": [108.2772, 14.0583],
          "Yemen": [48.5164, 15.5527],
          "Zambia": [27.8493, -13.1339],
          "Zimbabwe": [29.1549, -19.0154]
        };
        
        // Colors for the map markers
        const colors = ["#FF5733", "#33FF57", "#3357FF", "#FF33A8", "#33FFF6", 
                        "#F6FF33", "#A833FF", "#FF9433", "#33FFD8", "#8DFF33"];
        
        // Function to extract country from "City, Country" format
        const extractCountry = (location) => {
          if (!location) return 'Unknown';

          // Split by comma and get the last part (country)
          const parts = location.split(',');
          if (parts.length > 1) {
            // Return the last part trimmed (the country)
            return parts[parts.length - 1].trim();
          }

          // If no comma, return the whole string
          return location.trim();
        };
        // Group transactions by country
        addLotTransactions.forEach(tx => {
          // Extract country from location field
          const country = extractCountry(tx.location);

          if (country !== 'Unknown') {
            if (!countryMap.has(country)) {
              countryMap.set(country, {
                count: 0,
                volume: 0,
                color: colors[countryMap.size % colors.length]
              });
            }

            const countryData = countryMap.get(country);
            countryData.count += 1;
            

            // Extract numeric volume if possible
            let volume = 0;
            if (typeof tx.quantity === 'number') {
              volume = tx.quantity;
            } else if (typeof tx.quantity === 'string') {
              const volumeMatch = tx.quantity.match(/(\d+(\.\d+)?)/);
              if (volumeMatch) {
                volume = parseFloat(volumeMatch[1]);
              }
            } else if (typeof tx.Quantity === 'number') {
              volume = tx.Quantity;
            } else if (typeof tx.Quantity === 'string') {
              const volumeMatch = tx.Quantity.match(/(\d+(\.\d+)?)/);
              if (volumeMatch) {
                volume = parseFloat(volumeMatch[1]);
              }
            }

            // Add the volume to the country total
            countryData.volume += volume || 100; // Default to 100 if volume can't be parsed
          }
        });

        // Convert to map data format
        const newMapData = Array.from(countryMap.entries()).map(([country, data]) => {
          // Get coordinates for the country
          const coordinates = countryCoordinates[country];
          // If country not found in coordinates map, use a fallback
          if (!coordinates) {
            console.warn(`Coordinates not found for country: ${country}`);
          }

          return {
            name: country,
            coordinates: coordinates || [0, 0], // Fallback to [0,0] if not found
            volume: Math.max(data.volume, 100), // Ensure minimum size for visibility
            color: data.color
          };
        });

        // Filter out entries with [0,0] coordinates (unknown countries)
        const validMapData = newMapData.filter(item => !(item.coordinates[0] === 0 && item.coordinates[1] === 0));
        // Update map data state
        setMapData(validMapData.length > 0 ? validMapData : sampleMapData);
      } else {
        // Fall back to sample data if API response format is unexpected
        setMapData(sampleMapData);
      }
    } catch (error) {
      console.error('Error fetching map data:', error);
      // Fall back to sample data on error
      setMapData(sampleMapData);
    } finally {
      setIsMapLoading(false);
    }
  };

  // Call fetchMapData when the map tab is activated
  useEffect(() => {
    if (activeTab === 'map') {
      fetchMapData();
    }
  }, [activeTab]);

  const handleSearch = async () => {
    if (!inputValue) return;

    setIsLoading(true);
    setHasSearched(true); // Set to true when search is performed
    try {
      await dispatch(getFetchLedgerLot({ inputValue: String(inputValue) }));
      await dispatch(getFetchLedgerLotHistory({ inputValue: String(inputValue) }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    const certificateLink = `${window.location.origin}/certificate/${inputValue}`;
    navigator.clipboard.writeText(certificateLink);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (activeTab === "explorer" && inputValue && hasSearched) {
      handleSearch();
    }
  }, [selectedLanguage, activeTab]);

  const handleMarkerHover = (marker, event) => {
    setTooltipContent(
      intl.formatMessage(
        { id: "transactionexplorer.map.tooltipMarker", defaultMessage: "{name}: {volume} kg" },
        { name: marker.name, volume: marker.volume }
      )
    );
    setTooltipPosition({ x: event.clientX, y: event.clientY });
    setShowTooltip(true);
  };

  // Add a new state variable to store the real global stats
  const [globalStats, setGlobalStats] = useState({
    totalTransactions: 0,
    totalVolume: "0 kg",
    activeRegions: 0,
    averageTransactionSize: "0 kg"
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchGlobalStats = async () => {
    setStatsLoading(true);
    try {
      // Request a larger set of transactions for better statistical accuracy
      let params = new URLSearchParams({
        count: 500 // Get more transactions for better stats calculation
      }).toString();

      // Configure fetch options
      let options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "abc123",
          apikey: "abc123",
        },
      };

      // Make the API request
      const response = await fetch(`${BASE_URL}/getLatestTransactions?${params}`, options);
      const data = await response.json();
      console.log(" API response fetchGlobalStats:", data);

      if (data.status === "Success" && Array.isArray(data.result)) {
        const transactions = data.result;

        // Filter transactions that have productRefID
        const validTransactions = transactions.filter(tx => tx.productRefID);

        // Extract unique regions
        const regions = new Set();
        let totalVolume = 0;

        // For average price calculation
        let totalPrice = 0;
        let priceCount = 0;

        // Function to extract country from "City, Country" format
        const extractCountry = (location) => {
          if (!location || typeof location !== 'string') return 'Unknown';

          // Split by comma and get the last part (country)
          const parts = location.split(',');
          if (parts.length > 1) {
            return parts[parts.length - 1].trim();
          }


          return location.trim();
        };
        // Process transactions to calculate stats
        transactions.forEach(tx => {
          // Add region to set if location exists
          if (tx.location) {
            const country = extractCountry(tx.location);
            if (country !== 'Unknown') {
              regions.add(country);
            }
          }
          // Sum up volumes for transactions with UOM ending with MT
          if (tx.QuantityUOM && tx.QuantityUOM.endsWith('MT')) {
            let volume = 0;
            if (typeof tx.QuantityUOM === 'number') {
              volume = tx.QuantityUOM;
            } else if (typeof tx.QuantityUOM === 'string') {
              const volumeMatch = tx.Quantity;
              if (volumeMatch) {
                volume = parseFloat(volumeMatch);
              }
            }
            totalVolume += volume || 0;
          }
          // Calculate average for transactions with price ending with $/MT
          if (tx.price && typeof tx.price === 'string' && tx.price.endsWith('$/MT')) {
            const priceMatch = tx.price.match(/(\d+(\.\d+)?)/);
            if (priceMatch) {
              const price = parseFloat(priceMatch[1]);
              totalPrice += price;
              priceCount++;
            }
          }
        });
        // Format numbers with commas
        const formatNumber = (num) => {
          return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        // Calculate average price
        const avgPrice = priceCount > 0 ? totalPrice / priceCount : 0;


        // Update stats with calculated values
        setGlobalStats({
          totalTransactions: formatNumber(validTransactions.length),
          totalVolume: `${formatNumber(Math.round(totalVolume))} MT`,
          activeRegions: formatNumber(regions.size),
          averageTransactionSize: `${formatNumber(Math.round(avgPrice))} $/MT`
        });
      } else {
        // If API doesn't return the expected format, fall back to sample data
        setGlobalStats({
          totalTransactions: "15,783",
          totalVolume: "125,430 MT",
          activeRegions: "43",
          averageTransactionSize: "1,823 $/MT"
        });
      }
    } catch (error) {
      console.error('Error calculating stats from transactions:', error);
      // If all else fails, fall back to sample data
      setGlobalStats({
        totalTransactions: "15,783",
        totalVolume: "125,430 MT",
        activeRegions: "43",
        averageTransactionSize: "1,823 $/MT"
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch global stats on component mount
  useEffect(() => {
    fetchGlobalStats();
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setHasSearched(false); // Reset search state when input changes
  };

  return (
    <Box sx={styles.container}>
      {/* Quote Section */}
      <Box sx={styles.quoteSection}>
        <Title order={2} sx={styles.quoteText}>
          <FormattedMessage id="transactionexplorer.title" defaultMessage="Blockchain Explorer" />
        </Title>
        
        {/* Description text */}
        <Text sx={styles.descriptionText}>
          <FormattedMessage 
            id="transactionexplorer.description" 
            defaultMessage="Explore transactions, visualize global production, and track product movement in real-time through our blockchain network." 
          />
        </Text>
        
        {/* Decorative bar beneath the title/description */}
        <Box sx={styles.sectionDivider} />
      </Box>
      {/* Stats Cards */}
      <Container size="xl">
        <SimpleGrid cols={4} spacing="lg" breakpoints={[
          { maxWidth: 1200, cols: 2 },
          { maxWidth: 600, cols: 1 }
        ]}>
          <Card shadow="sm" p="lg" radius="md" withBorder sx={styles.statsCard}>
            <Text weight={500} size="lg">
              <FormattedMessage id="transactionexplorer.stats.totalTransactions" defaultMessage="Total Transactions" />
            </Text>
            {statsLoading ? (
              <Group position="left" mt="md">
                <Loader size="sm" />
              </Group>
            ) : (
              <Text weight={700} size="xl" color="blue">{globalStats.totalTransactions}</Text>
            )}
          </Card>
          <Card shadow="sm" p="lg" radius="md" withBorder sx={styles.statsCard}>
            <Text weight={500} size="lg">
              <FormattedMessage id="transactionexplorer.stats.totalVolume" defaultMessage="Total Volume" />
            </Text>
            {statsLoading ? (
              <Group position="left" mt="md">
                <Loader size="sm" />
              </Group>
            ) : (
              <Text weight={700} size="xl" color="blue">{globalStats.totalVolume}</Text>
            )}
          </Card>
          <Card shadow="sm" p="lg" radius="md" withBorder sx={styles.statsCard}>
            <Text weight={500} size="lg">
              <FormattedMessage id="transactionexplorer.stats.activeRegions" defaultMessage="Active Regions" />
            </Text>
            {statsLoading ? (
              <Group position="left" mt="md">
                <Loader size="sm" />
              </Group>
            ) : (
              <Text weight={700} size="xl" color="blue">{globalStats.activeRegions}</Text>
            )}
          </Card>
          <Card shadow="sm" p="lg" radius="md" withBorder sx={styles.statsCard}>
            <Text weight={500} size="lg">
              <FormattedMessage id="transactionexplorer.stats.averageSize" defaultMessage="Average Transaction" />
            </Text>
            {statsLoading ? (
              <Group position="left" mt="md">
                <Loader size="sm" />
              </Group>
            ) : (
              <Text weight={700} size="xl" color="blue">{globalStats.averageTransactionSize}</Text>
            )}
          </Card>
        </SimpleGrid>
      </Container>
      {/* Main Content with Tabs */}
      <Container size="xl">
        <Box sx={styles.tabsContainer}>
          <Tabs value={activeTab} onTabChange={setActiveTab}>
            <Tabs.List grow position="center">
              <Tabs.Tab 
                value="transactions"
                icon={<FaHistory size={18} />}
              >
                <FormattedMessage id="transactionexplorer.tabs.recentTransactions" defaultMessage="Recent Transactions" />
              </Tabs.Tab>
              <Tabs.Tab
                value="explorer"
                icon={<FaCertificate size={18} />}
              >
                <FormattedMessage id="transactionexplorer.tabs.productExplorer" defaultMessage="Product Explorer" />
              </Tabs.Tab>
              <Tabs.Tab 
                value="map"
                icon={<FaMapMarkedAlt size={18} />}
              >
                <FormattedMessage id="transactionexplorer.tabs.globalMap" defaultMessage="Global Production Map" />
              </Tabs.Tab>
            </Tabs.List>

            {/* Product Explorer Tab */}
            <Tabs.Panel value="explorer" pt="xl">
              <Box sx={styles.mainContent}>
                <Box sx={styles.searchContainer}>
                  <TextInput
                    placeholder={intl.formatMessage({ id: "transactionexplorer.search.placeholder", defaultMessage: "Enter product ID or transaction hash" })}
                    value={inputValue}
                    onChange={handleInputChange} 
                    onKeyPress={handleKeyPress}
                    style={{ flexGrow: 1 }}
                    size="md"
                  />
                  <MantineButton 
                    variant="filled" 
                    onClick={handleSearch}
                    loading={isLoading}
                    size="md"
                    color="blue"
                  >
                    <FaSearch />
                  </MantineButton>
                </Box>
                {/* Results Section */}
                {hasSearched && inputValue && (
                  <Box sx={styles.resultContainer}>
                    <Box sx={styles.copyButtonContainer}>
                      <Button
                        variant="contained"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyLink}
                        color="primary"
                        size="large"
                      >
                        <FormattedMessage id="transactionexplorer.button.copyLink" defaultMessage="Copy Certificate Link" />
                      </Button>
                    </Box>
                    <CertificatePage productId={inputValue} fromExplorer={true} />
                  </Box>
                )}
              </Box>
            </Tabs.Panel>

            {/* Global Map Tab */}
            <Tabs.Panel value="map" pt="xl">
              <Box>
                <Text align="center" size="lg" weight={500} mb="md">
                  <FormattedMessage id="transactionexplorer.map.title" defaultMessage="Global Production Volume Visualization" />
                </Text>
                <Text align="center" color="dimmed" mb="xl">
                  <FormattedMessage id="transactionexplorer.map.description" defaultMessage="Visualization of production volumes across different regions. Circle size indicates volume." />
                </Text>
                <Box sx={styles.mapContainer}>
                  {isMapLoading && (
                    <Box sx={styles.mapLoaderContainer}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Loader size="md" />
                        <Text mt="xs">
                          <FormattedMessage id="transactionexplorer.map.loading" defaultMessage="Loading map data..." />
                        </Text>
                      </Box>
                    </Box>
                  )}
                  <ComposableMap projection="geoEqualEarth">
                    <ZoomableGroup center={[0, 0]} zoom={1}>
                      {/* Render GeoJSON if available */}
                      {geoData && (
                        <Geographies geography={geoData}>
                          {({ geographies }) =>
                            geographies.map((geo) => (
                              <Geography
                                key={geo.rsmKey || geo.id || Math.random()}
                                geography={geo}
                                fill="#EAEAEC"
                                stroke="#D6D6DA"
                                strokeWidth={0.5}
                              />
                            ))
                          }
                        </Geographies>
                      )}
                      {/* Fallback basic world outline if no GeoJSON */}
                      {!geoData && !isMapLoading && (
                        <>
                          <path
                            d="M-180,-90 L180,-90 L180,90 L-180,90 Z"
                            fill="#e4e6e7"
                            stroke="#d1d5db"
                            strokeWidth={1}
                          />
                          <path 
                            d="M-60,0 C-20,30 20,30 60,0 C20,-30 -20,-30 -60,0" 
                            fill="#cfd3d7" 
                            stroke="#aeb3b9" 
                            strokeWidth={0.5}
                          />
                          <path 
                            d="M60,20 C80,10 100,10 120,20 C100,30 80,30 60,20" 
                            fill="#cfd3d7" 
                            stroke="#aeb3b9" 
                            strokeWidth={0.5}
                          />
                          <path 
                            d="M-120,20 C-100,10 -80,10 -60,20 C-80,30 -100,30 -120,20" 
                            fill="#cfd3d7" 
                            stroke="#aeb3b9"
                            strokeWidth={0.5}
                          />
                        </>
                      )}
                      {/* Always show markers */}
                      {mapData.map((marker) => (
                        <Marker 
                          key={marker.name}
                          coordinates={marker.coordinates}
                          onMouseEnter={(e) => handleMarkerHover(marker, e)}
                          onMouseLeave={() => setShowTooltip(false)}
                        >
                          <circle
                            r={Math.sqrt(marker.volume) / 10}
                            fill={marker.color}
                            stroke="#fff"
                            strokeWidth={2}
                            opacity={0.8}
                          />
                          <text
                            textAnchor="middle"
                            y={-10}
                            style={{ 
                              fontFamily: "system-ui", 
                              fontSize: "8px",
                              fontWeight: "bold",
                              fill: "#333"
                            }}
                          >
                            {marker.name}
                          </text>
                        </Marker>
                      ))}
                    </ZoomableGroup>
                  </ComposableMap>
                  {showTooltip && (
                    <div
                      style={{
                        position: 'absolute',
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                        padding: '5px 10px',
                        background: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        zIndex: 1000
                      }}
                    >
                      {tooltipContent}
                    </div>
                  )}
                </Box>
                <Text align="center" size="sm" color="dimmed" mt="md">
                  <FormattedMessage id="transactionexplorer.map.hover" defaultMessage="Hover over markers to see details" />
                </Text>
              </Box>
            </Tabs.Panel>

            {/* Recent Transactions Tab */}
            <Tabs.Panel value="transactions" pt="xl">
              <Box sx={styles.transactionList}>
                <Text align="center" size="lg" weight={500} mb="md">
                  <FormattedMessage id="transactionexplorer.transactions.title" defaultMessage="Recent Blockchain Transactions" />
                </Text>
                <Text align="center" color="dimmed" mb="xl">
                  <FormattedMessage id="transactionexplorer.transactions.description" defaultMessage="Latest transactions recorded on our blockchain network" />
                </Text>
                <ScrollArea viewportRef={scrollViewportRef} style={{ height: '400px' }}>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.hash" defaultMessage="Transaction Hash" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.sku" defaultMessage="ID" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.product" defaultMessage="Product" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.location" defaultMessage="Location" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.timestamp" defaultMessage="Timestamp" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.amount" defaultMessage="Amount" /></th>
                        <th><FormattedMessage id="transactionexplorer.transactions.table.status" defaultMessage="Status" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsLoading ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Loader size="sm" />
                            <Text size="sm" mt={5}>
                              <FormattedMessage id="transactionexplorer.transactions.loading" defaultMessage="Loading transactions..." />
                            </Text>
                          </td>
                        </tr>
                      ) : transactions.length > 0 ? (
                        transactions.map((tx) => (
                          <tr key={tx.id}>
                            <td>
                              <Group spacing="xs">
                                <Button
                                  variant="text"
                                  color="primary"
                                  component="a"
                                  href={`https://hashscan.io/${network}/${tx.ownerAccountId ? 'account' : 'transaction'}/${tx.ownerAccountId || tx.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ padding: '0', textTransform: 'none' }}
                                >
                                  {tx.id.substring(0, 6) + '...' + tx.id.substring(tx.id.length - 4)}
                                </Button>
                                <ContentCopy 
                                  style={{ fontSize: '14px', cursor: 'pointer' }}
                                  onClick={() => navigator.clipboard.writeText(tx.id)}
                                />
                              </Group>
                            </td>
                            <td>{tx.sku || tx.Sku || <FormattedMessage id="transactionexplorer.transactions.unknown" defaultMessage="Unknown" />}</td>
                            <td>{tx.productName || tx["Product Name"] || tx.categoryName || <FormattedMessage id="transactionexplorer.transactions.unknown" defaultMessage="Unknown" />}</td>
                            <td>{tx.location || <FormattedMessage id="transactionexplorer.transactions.unknown" defaultMessage="Unknown" />}</td>
                            <td>{tx._ts ? new Date(tx._ts * 1000).toLocaleString() : <FormattedMessage id="transactionexplorer.transactions.unknown" defaultMessage="Unknown" />}</td>
                            <td>{tx.QuantityUOM || tx.quantity || <FormattedMessage id="transactionexplorer.transactions.unknown" defaultMessage="Unknown" />}</td>
                            <td>
                              <Badge 
                                color={tx.event === "updateLotState" ? "blue" : 
                                    tx.event === "addLot" ? "green" : 
                                    tx.event === "transformLot" ? "orange" : 
                                    tx.event === "transportLot" ? "indigo" : 
                                    "gray"}
                                variant="filled"
                              >
                                {tx.event || <FormattedMessage id="transactionexplorer.transactions.defaultEvent" defaultMessage="Transaction" />}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '20px 0' }}>
                          <Text>
                            <FormattedMessage id="transactionexplorer.transactions.noTransactions" defaultMessage="No transactions found" />
                          </Text>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {isLoadingMore && (
                    <Box style={{ textAlign: 'center', padding: '20px 0' }}>
                      <Loader size="sm" />
                      <Text size="sm" mt={5}>
                        <FormattedMessage id="transactionexplorer.transactions.loadingMore" defaultMessage="Loading more transactions..." />
                      </Text>
                    </Box>
                  )}
                  {!hasMoreTransactions && transactions.length > 0 && (
                    <Box style={{ textAlign: 'center', padding: '20px 0' }}>
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="transactionexplorer.transactions.noMore" defaultMessage="No more transactions to load" />
                      </Text>
                    </Box>
                  )}
                </ScrollArea>
              </Box>
            </Tabs.Panel>
          </Tabs>
        </Box>
      </Container>
      <LoginModal />
    </Box>
  );
};

export default EnhancedTransactionExplorer;